import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaDesktop, FaUsers, FaBullhorn, FaSearchDollar, FaEnvelope, FaMobileAlt, FaCamera, FaEnvelopeOpenText, FaWordpress, FaPaperPlane, FaServer, FaTrademark, FaGoogle } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

export const ServicesPageContainer = styled.div`
  padding: 8rem 2rem; /* Add padding to account for fixed header */
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

export const SectionTitle = styled.h2`
  font-size: 3.5rem;
  color: #1f2937;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
    @media (max-width: 768px) {
      font-size: 3.2rem;
  }

`;

export const SectionSubtitle = styled.p`
  font-size: 1.4rem;
  color: #4b5563;
  margin-bottom: 4rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  padding: 0 1rem;
`;

export const ServiceCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  padding: 3rem; /* Increased padding for larger cards */
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.1s ease-in-out;
  height: 400px; /* Ensure consistent height for all cards */

  h3 {
    font-size: 1.8rem; /* Larger font size for titles */
    margin-bottom: 1rem;
    color:rgb(80, 80, 80); /* Keep initial color as dark text */
    transition: color 0.3s ease-in-out;
  }

  p {
    font-size: 1.1rem; /* Larger font size for descriptions */
    color: #4b5563;
    line-height: 1.7;
    transition: color 0.3s ease-in-out;
  }

  svg {
    margin-bottom: 1.5rem; /* Increased margin for icons */
    color: #00B4DB; /* Default icon color */
    transition: color 0.3s ease-in-out;
    font-size: 50px; /* Larger icon size */
  }

  &:hover {
    background: linear-gradient(45deg, #00B4DB, #007bff);
    border-color: rgba(0, 180, 219, 0.5);
    box-shadow: 0 8px 30px rgba(0, 180, 219, 0.4);
    transform: translateY(-10px) scale(1.02);

    h3,
    p,
    svg {
      color: white !important;
    }
  }
`;

export const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

export const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 2rem;
`;

export const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

export const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

export const SubmitButton = styled(motion.button)`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function ServicesPage() {
  const [quickContactData, setQuickContactData] = useState({
    name: '',
    phone: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setQuickContactData({
          ...quickContactData,
          [name]: numbersOnly
        });
      }
    } else {
      setQuickContactData({
        ...quickContactData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (quickContactData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: quickContactData.name,
        phone: quickContactData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setQuickContactData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  const services = [
    { icon: <FaDesktop />, title: "Kurumsal Web Tasarım", description: "Şirketinizin dijital varlığını güçlendirecek modern ve kullanıcı dostu web siteleri tasarlıyoruz.", path: "/services/kurumsal-web-tasarim" },
    { icon: <FaMobileAlt />, title: "Mobil Uygulama", description: "İşletmenize özel, kullanıcı dostu ve yenilikçi mobil uygulamalar geliştiriyoruz.", path: "/services/mobil-uygulama" },
    { icon: <FaCamera />, title: "Fotoğraf Çekimi", description: "Ürünleriniz ve hizmetleriniz için profesyonel fotoğraf çekimi hizmetleri sunuyoruz.", path: "/services/fotograf-cekimi" },
    { icon: <FaServer />, title: "Sunucu Hizmetleri", description: "Yüksek performanslı, güvenli ve ölçeklenebilir sunucu çözümleri ile işletmenizin dijital altyapısını güçlendiriyoruz.", path: "/services/sunucu-hizmetleri" },
    { icon: <FaTrademark />, title: "Marka Tescil Hizmeti", description: "Markanızı koruma altına alıyor, tescil süreçlerinizi profesyonel bir şekilde yönetiyoruz.", path: "/services/marka-tescil-hizmeti" },
    { icon: <FaGoogle />, title: "Google İşletme Kaydı", description: "Google My Business hesabınızı oluşturup yöneterek, yerel aramalarda öne çıkmanızı sağlıyoruz.", path: "/services/google-isletme-kaydi" },
    { icon: <FaUsers />, title: "Sosyal Medya Yönetimi", description: "Markanızın sosyal medyadaki varlığını güçlendiriyor, hedef kitlenizle etkileşimi artırıyoruz.", path: "/services/sosyal-medya-yonetimi" },
    { icon: <FaSearchDollar />, title: "Performans Odaklı SEO", description: "Arama motorlarında üst sıralara çıkarak organik trafiğinizi ve marka bilinirliğinizi artırıyoruz.", path: "/services/performans-odakli-seo" },
    { icon: <FaWordpress />, title: "WordPress Hizmetleri", description: "WordPress tabanlı web siteleriniz için özelleştirme, bakım ve güvenlik hizmetleri sunuyoruz.", path: "/services/wordpress-hizmetleri" },
    { icon: <FaEnvelope />, title: "Kurumsal Mail Hizmeti", description: "Profesyonel ve güvenli e-posta çözümleriyle kurumsal iletişiminizi sorunsuz hale getiriyoruz.", path: "/services/kurumsal-mail-hizmeti" },
    { icon: <FaEnvelopeOpenText />, title: "E-Posta Pazarlama", description: "Hedef kitlenize ulaşan, etkili e-posta kampanyaları ile satışlarınızı artırıyoruz.", path: "/services/e-posta-pazarlama" },
    { icon: <FaBullhorn />, title: "Reklam Yönetimi", description: "Google Ads ve sosyal medya reklamlarıyla hedef kitlenize ulaşarak dönüşümlerinizi artırıyoruz.", path: "/services/reklam-yonetimi" }
  ];

  return (
    <ServicesPageContainer>
      <SectionTitle>Hizmetlerimiz</SectionTitle>
      <SectionSubtitle>İşletmenizi dijitalde bir adım öne taşıyacak kapsamlı ve yenilikçi çözümler sunuyoruz.</SectionSubtitle>
      <ServicesGrid>
        {services.map((service, index) => (
          <Link to={service.path} key={index} style={{ textDecoration: 'none' }}>
            <ServiceCard
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {service.icon}
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </ServiceCard>
          </Link>
        ))}
      </ServicesGrid>
      
      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={quickContactData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={quickContactData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServicesPageContainer>
  );
}

export default ServicesPage; 